import type {
  PblAuthUser,
  PblCase,
  PblCitizenContext,
  PblConsultationMethod,
  PblPrimaryLegalServiceNeed,
  PblServiceArea,
} from '@/types/pbl';
import type { PblCitizenCaseCreatePayload, PblCitizenCaseDraftPayload } from '@/types/pbl';
import { formatPblEnglishChineseName } from '@/utils/pblDisplay';

export type CitizenCaseUploadedDocument = {
  storage_file_id: number;
  title: string;
  document_type: string;
  metadata?: Record<string, unknown>;
};

export type CitizenCaseApplicationForm = {
  pbl_citizen_profile_id: number;
  pbl_oc_office_id: number;
  oc_office_name: string;
  assisting_staff_name: string;
  office_contact: string;
  citizen_full_name: string;
  citizen_full_name_en: string;
  citizen_full_name_zh: string;
  identity_partial: string;
  phone: string;
  email: string;
  residential_address: string;
  enquiry_category: PblServiceArea | '';
  urgency: 'normal' | 'urgent';
  court_deadline: string;
  factual_background: string;
  specific_queries: string;
  primary_legal_service_needs: PblPrimaryLegalServiceNeed[];
  primary_legal_service_needs_other: string;
  consultation_methods: PblConsultationMethod[];
  disclaimer_acknowledged: boolean;
  enquirer_signature: string;
  enquirer_signature_file_id?: number;
  enquirer_signed_at: string;
  uploaded_documents: CitizenCaseUploadedDocument[];
};

type CitizenCaseSignatureUpdate = {
  dataUrl: string;
  fileId: number;
  signedAt: string;
};

export const CITIZEN_CASE_FACTUAL_BACKGROUND_RECOMMENDATION = 'Recommended 50-500 words.';

export const initialCitizenCaseForm: CitizenCaseApplicationForm = {
  pbl_citizen_profile_id: 0,
  pbl_oc_office_id: 0,
  oc_office_name: '',
  assisting_staff_name: '',
  office_contact: '',
  citizen_full_name: '',
  citizen_full_name_en: '',
  citizen_full_name_zh: '',
  identity_partial: '',
  phone: '',
  email: '',
  residential_address: '',
  enquiry_category: '',
  urgency: 'normal',
  court_deadline: '',
  factual_background: '',
  specific_queries: '',
  primary_legal_service_needs: [],
  primary_legal_service_needs_other: '',
  consultation_methods: ['video_call'],
  disclaimer_acknowledged: true,
  enquirer_signature: '',
  enquirer_signed_at: '',
  uploaded_documents: [],
};

const hasText = (value: string) => value.trim().length > 0;
const hasFileId = (value?: number) => typeof value === 'number' && Number.isFinite(value);

export const isCitizenCaseFactualBackgroundValid = (value: string) =>
  hasText(value);

export const buildInitialCitizenCaseForm = (userData?: Pick<PblAuthUser, 'pbl_citizen_profile'> | null) => {
  const profile = userData?.pbl_citizen_profile;

  if (!profile) return initialCitizenCaseForm;

  const citizenFullNameEn = profile.citizen_full_name_en || '';
  const citizenFullNameZh = profile.citizen_full_name_zh || '';
  const citizenFullName = formatPblEnglishChineseName(
    citizenFullNameEn,
    citizenFullNameZh,
    profile.citizen_full_name
  );
  const ocOffice = profile.oc_office;

  return {
    ...initialCitizenCaseForm,
    pbl_citizen_profile_id: profile.id,
    pbl_oc_office_id: profile.pbl_oc_office_id,
    oc_office_name: profile.oc_office_name || ocOffice?.office_name || ocOffice?.name || '',
    assisting_staff_name: profile.assisting_staff_name || '',
    office_contact: profile.office_contact || ocOffice?.office_contact || ocOffice?.contact || '',
    citizen_full_name: citizenFullName,
    citizen_full_name_en: citizenFullNameEn,
    citizen_full_name_zh: citizenFullNameZh,
    identity_partial: profile.identity_partial || '',
    phone: profile.phone || '',
    email: profile.email || '',
    residential_address: profile.residential_address || '',
  };
};

export const mergeCitizenContextIntoCaseForm = (
  form: CitizenCaseApplicationForm,
  context: PblCitizenContext
): CitizenCaseApplicationForm => {
  const office = context.default_office;
  const readonlyForm = buildInitialCitizenCaseForm({
    pbl_citizen_profile: {
      ...context.citizen_profile,
      pbl_oc_office_id: office?.pbl_oc_office_id ?? context.citizen_profile.pbl_oc_office_id,
      oc_office_name: office?.oc_office_name ?? context.citizen_profile.oc_office_name,
      assisting_staff_name: office?.assisting_staff_name ?? context.citizen_profile.assisting_staff_name,
      office_contact: office?.office_contact ?? context.citizen_profile.office_contact,
    },
  });

  return {
    ...form,
    pbl_citizen_profile_id: readonlyForm.pbl_citizen_profile_id,
    pbl_oc_office_id: readonlyForm.pbl_oc_office_id,
    oc_office_name: readonlyForm.oc_office_name,
    assisting_staff_name: readonlyForm.assisting_staff_name,
    office_contact: readonlyForm.office_contact,
    citizen_full_name: readonlyForm.citizen_full_name,
    citizen_full_name_en: readonlyForm.citizen_full_name_en,
    citizen_full_name_zh: readonlyForm.citizen_full_name_zh,
    identity_partial: readonlyForm.identity_partial,
    phone: readonlyForm.phone,
    email: readonlyForm.email,
    residential_address: readonlyForm.residential_address,
  };
};


export const mergeCitizenCaseIntoApplicationForm = (
  form: CitizenCaseApplicationForm,
  caseData: PblCase
): CitizenCaseApplicationForm => ({
  ...form,
  enquiry_category: (caseData.enquiry_category as PblServiceArea) || form.enquiry_category,
  urgency: caseData.urgency === 'urgent' || caseData.urgency === 'normal' ? caseData.urgency : form.urgency,
  court_deadline: caseData.court_deadline === undefined ? form.court_deadline : (caseData.court_deadline ?? ''),
  factual_background: caseData.factual_background || '',
  specific_queries: caseData.specific_queries || '',
  primary_legal_service_needs: (caseData.primary_legal_service_needs || []) as PblPrimaryLegalServiceNeed[],
  primary_legal_service_needs_other: caseData.primary_legal_service_needs_other || '',
  consultation_methods: Array.isArray(caseData.preferred_consultation_method)
    ? (caseData.preferred_consultation_method as PblConsultationMethod[])
    : caseData.preferred_consultation_method
      ? [caseData.preferred_consultation_method as PblConsultationMethod]
      : form.consultation_methods,
  disclaimer_acknowledged: Boolean(caseData.case_disclaimer_acknowledged ?? form.disclaimer_acknowledged),
  enquirer_signature: caseData.case_enquirer_signature || form.enquirer_signature,
  enquirer_signature_file_id: caseData.case_enquirer_signature_file_id || form.enquirer_signature_file_id,
  enquirer_signed_at: caseData.case_enquirer_signed_at || form.enquirer_signed_at,
  uploaded_documents: (caseData.documents || [])
    .map((document) => ({
      storage_file_id: Number(document.storage_file_id || document.storage_file?.id),
      title: document.title || document.storage_file?.name || 'Document',
      document_type: document.document_type || 'case_supporting_document',
      metadata: document.metadata || undefined,
    }))
    .filter((document) => Number.isFinite(document.storage_file_id)),
});

export const isCitizenCaseApplicationComplete = (form: CitizenCaseApplicationForm) =>
  Boolean(form.enquiry_category) &&
  isCitizenCaseFactualBackgroundValid(form.factual_background) &&
  hasText(form.specific_queries) &&
  form.primary_legal_service_needs.length > 0 &&
  form.consultation_methods.length === 1 &&
  form.disclaimer_acknowledged &&
  hasText(form.enquirer_signature) &&
  hasFileId(form.enquirer_signature_file_id) &&
  hasText(form.enquirer_signed_at);

export const signCitizenCaseApplication = (
  form: CitizenCaseApplicationForm,
  signature: CitizenCaseSignatureUpdate
): CitizenCaseApplicationForm => ({
  ...form,
  enquirer_signature: signature.dataUrl,
  enquirer_signature_file_id: signature.fileId,
  enquirer_signed_at: signature.signedAt,
});

export const buildCitizenCasePayload = (form: CitizenCaseApplicationForm): PblCitizenCaseCreatePayload => ({
  enquiry_category: form.enquiry_category,
  urgency: form.urgency,
  court_deadline: form.court_deadline || null,
  factual_background: form.factual_background,
  specific_queries: form.specific_queries,
  primary_legal_service_needs: form.primary_legal_service_needs,
  primary_legal_service_needs_other:
    form.primary_legal_service_needs.includes('other') && form.primary_legal_service_needs_other.trim()
      ? form.primary_legal_service_needs_other.trim()
      : null,
  preferred_consultation_method: form.consultation_methods[0] as PblConsultationMethod,
  case_disclaimer_acknowledged: form.disclaimer_acknowledged,
  case_enquirer_signature_file_id: form.enquirer_signature_file_id,
  case_enquirer_signed_at: form.enquirer_signed_at,
  ...(form.uploaded_documents.length ? { documents: form.uploaded_documents } : {}),
});


export const buildCitizenCaseDraftPayload = (form: CitizenCaseApplicationForm): PblCitizenCaseDraftPayload => ({
  enquiry_category: form.enquiry_category || '',
  urgency: form.urgency,
  court_deadline: form.court_deadline || null,
  factual_background: form.factual_background,
  specific_queries: form.specific_queries,
  primary_legal_service_needs: form.primary_legal_service_needs,
  primary_legal_service_needs_other:
    form.primary_legal_service_needs.includes('other') && form.primary_legal_service_needs_other.trim()
      ? form.primary_legal_service_needs_other.trim()
      : null,
  preferred_consultation_method: form.consultation_methods[0] || '',
  case_disclaimer_acknowledged: form.disclaimer_acknowledged,
  case_enquirer_signature_file_id: form.enquirer_signature_file_id,
  case_enquirer_signed_at: form.enquirer_signed_at,
  ...(form.uploaded_documents.length ? { documents: form.uploaded_documents } : {}),
});
